'use client'

import Modal from '@/components/Modal/Modal'
import { useCreateScheduledTaskMutation } from '@/lib/features/scheduled-tasks/scheduledTasksApi'
import { useGetUserQuery } from '@/lib/features/user/userApi'
import { cn } from '@/lib/utils'
import { getToolDisplay } from '@/utils/copilotToolMappings'
import { motion } from 'framer-motion'
import { Bell, Calendar, Pencil, Star, X } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import type { RepeatSpec } from 'sprites-types/api/v2/scheduled-tasks'
import {
  ScheduleEditor,
  buildRepeatSpec,
  getFirstRunAtISO,
  getUserTimezone,
  repeatSpecToEditorState,
  type IntervalUnit,
  type MonthWeekdayRule,
  type SchedulePreset,
} from './ScheduleEditor'
import { ToolChip } from './ToolChip'

interface ScheduleTaskPrefill {
  name?: string
  description?: string | null
  toolCalls?: Array<{
    function: {
      name: string
      arguments: Record<string, unknown>
    }
  }>
  repeatSpec?: RepeatSpec | null
  timezone?: string | null
  maxRuns?: number | null
  startDate?: string | null
  startHour?: number | null
}

interface ScheduleTaskModalProps {
  isOpen: boolean
  onClose: () => void
  toolCall?: {
    function: {
      name: string
      arguments: Record<string, unknown>
    }
  }
  toolCalls?: Array<{
    function: {
      name: string
      arguments: Record<string, unknown>
    }
  }>
  toolName: string
  prefill?: ScheduleTaskPrefill
}

export function ScheduleTaskModal({
  isOpen,
  onClose,
  toolCall,
  toolCalls,
  toolName,
  prefill,
}: ScheduleTaskModalProps) {
  const resolvedToolCalls = prefill?.toolCalls ?? toolCalls ?? (toolCall ? [toolCall] : [])

  // Get default date/time (tomorrow at 9 AM)
  const tomorrow = new Date()
  tomorrow.setDate(tomorrow.getDate() + 1)
  tomorrow.setHours(9, 0, 0, 0)

  // Form state
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [startDate, setStartDate] = useState(tomorrow.toISOString().split('T')[0])
  const [startHour, setStartHour] = useState(9)
  const [timezone, setTimezone] = useState(() => getUserTimezone())

  // Schedule configuration - using the new unified model
  const [preset, setPreset] = useState<SchedulePreset>('daily')
  const [interval, setInterval] = useState(1)
  const [intervalUnit, setIntervalUnit] = useState<IntervalUnit>('day')
  const [daysOfWeek, setDaysOfWeek] = useState<number[]>([])
  const [monthDayOfMonth, setMonthDayOfMonth] = useState<number | null>(null)
  const [monthWeekdayRule, setMonthWeekdayRule] = useState<MonthWeekdayRule | null>(null)

  // End conditions
  const [maxRuns, setMaxRuns] = useState<number | null>(null)
  const [endMode, setEndMode] = useState<'none' | 'maxRuns' | 'endDate'>('none')
  const [endDate, setEndDate] = useState<string>('')

  // Favourite and notification state
  const [bookmark, setBookmark] = useState(false)
  const [emailNotification, setEmailNotification] = useState(false)

  // UI state
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isEditingName, setIsEditingName] = useState(true)
  const [isEditingDescription, setIsEditingDescription] = useState(false)
  const [openToolIndex, setOpenToolIndex] = useState<number | null>(null)
  const nameInputRef = useRef<HTMLInputElement>(null)
  const descriptionTextareaRef = useRef<HTMLTextAreaElement>(null)

  useGetUserQuery()
  const [createTask] = useCreateScheduledTaskMutation()

  // Apply prefill values when modal opens with prefill data
  useEffect(() => {
    if (isOpen && prefill) {
      // Apply name and description
      if (prefill.name) {
        setName(prefill.name)
        setIsEditingName(false)
      }
      if (prefill.description) {
        setDescription(prefill.description)
      }
      if (prefill.timezone) {
        setTimezone(prefill.timezone)
      }
      if (prefill.maxRuns) {
        setMaxRuns(prefill.maxRuns)
        setEndMode('maxRuns')
      }

      // Apply repeatSpec using the conversion function (UTC → local for display)
      const tz = prefill.timezone ?? undefined
      const editorState = repeatSpecToEditorState(prefill.repeatSpec, tz)
      setPreset(editorState.preset)
      setInterval(editorState.interval)
      setIntervalUnit(editorState.intervalUnit)
      setStartHour(prefill.startHour ?? editorState.startHour)
      setDaysOfWeek(editorState.daysOfWeek)
      setMonthDayOfMonth(editorState.monthDayOfMonth)
      setMonthWeekdayRule(editorState.monthWeekdayRule)
      if (prefill.startDate) {
        setStartDate(prefill.startDate)
      }
    }
  }, [isOpen, prefill])

  // Focus name input when modal opens
  useEffect(() => {
    if (isOpen && nameInputRef.current) {
      nameInputRef.current.focus()
      nameInputRef.current.select()
    }
  }, [isOpen])

  // Focus description textarea when entering edit mode
  useEffect(() => {
    if (isEditingDescription && descriptionTextareaRef.current) {
      descriptionTextareaRef.current.focus()
    }
  }, [isEditingDescription])

  // Build the repeatSpec from current state (local hours → UTC)
  const repeatSpec = useMemo(
    () =>
      buildRepeatSpec({
        preset,
        startHour,
        interval,
        intervalUnit,
        daysOfWeek,
        monthDayOfMonth,
        monthWeekdayRule,
        timezone,
      }),
    [
      preset,
      startHour,
      interval,
      intervalUnit,
      daysOfWeek,
      monthDayOfMonth,
      monthWeekdayRule,
      timezone,
    ]
  )

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)

    try {
      let resolvedMaxRuns: number | null = null

      if (endMode === 'maxRuns' && maxRuns && maxRuns > 0) {
        resolvedMaxRuns = maxRuns
      } else if (endMode === 'endDate' && endDate) {
        const start = new Date(startDate)
        const end = new Date(endDate)

        if (end < start) {
          toast.error('End date must be on or after the start date.')
          setIsSubmitting(false)
          return
        }

        const startMidnight = new Date(
          start.getFullYear(),
          start.getMonth(),
          start.getDate(),
          0,
          0,
          0,
          0
        )
        const endMidnight = new Date(end.getFullYear(), end.getMonth(), end.getDate(), 0, 0, 0, 0)

        const diffDays = Math.floor(
          (endMidnight.getTime() - startMidnight.getTime()) / (1000 * 60 * 60 * 24)
        )

        // Calculate max runs based on schedule
        if (preset === 'never') {
          resolvedMaxRuns = endMidnight >= startMidnight ? 1 : 0
        } else if (
          preset === 'daily' ||
          (preset === 'custom' && intervalUnit === 'day' && interval === 1)
        ) {
          resolvedMaxRuns = diffDays + 1
        } else if (preset === 'custom' && intervalUnit === 'day') {
          resolvedMaxRuns = Math.floor(diffDays / interval) + 1
        } else if (preset === 'weekly' || (preset === 'custom' && intervalUnit === 'week')) {
          const daySet = new Set(daysOfWeek.length > 0 ? daysOfWeek : [start.getDay()])
          const weekInterval = preset === 'weekly' ? 1 : interval
          let count = 0
          const cursor = new Date(startMidnight)
          while (cursor <= endMidnight) {
            if (daySet.has(cursor.getDay())) {
              const daysSinceStart = Math.round(
                (cursor.getTime() - startMidnight.getTime()) / (24 * 60 * 60 * 1000)
              )
              const weeksSinceStart = Math.floor(daysSinceStart / 7)
              if (weeksSinceStart % weekInterval === 0) count += 1
            }
            cursor.setDate(cursor.getDate() + 1)
          }
          resolvedMaxRuns = count
        } else if (preset === 'custom' && intervalUnit === 'month') {
          let count = 0
          const cursor = new Date(startMidnight)
          const targetDay = start.getDate()
          const daysInMonth = (d: Date) => new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate()
          while (cursor <= endMidnight) {
            count += 1
            cursor.setMonth(cursor.getMonth() + interval)
            cursor.setDate(Math.min(targetDay, daysInMonth(cursor)))
          }
          resolvedMaxRuns = count
        }

        if (resolvedMaxRuns !== null && resolvedMaxRuns <= 0) {
          toast.error('End date does not allow any runs with the selected schedule.')
          setIsSubmitting(false)
          return
        }
      }

      const defaultToolDisplayName = getToolDisplay(toolName).displayName
      const firstRunAt = getFirstRunAtISO(startDate, startHour, timezone)
      await createTask({
        name:
          name ||
          (resolvedToolCalls.length > 1
            ? `${resolvedToolCalls.length} tools - Scheduled`
            : `${defaultToolDisplayName} - Scheduled`),
        description: description || null,
        toolCalls: resolvedToolCalls.map((tc) => ({
          function: {
            name: tc.function.name,
            arguments: tc.function.arguments,
          },
        })),
        timezone: timezone || null,
        repeatSpec,
        maxRuns: resolvedMaxRuns,
        firstRunAt,
        bookmark,
        notificationSettings: emailNotification ? { email: { enabled: true } } : null,
      }).unwrap()

      toast.success('Task scheduled successfully')
      onClose()

      // Reset form
      setName('')
      setDescription('')
      setStartDate(tomorrow.toISOString().split('T')[0])
      setStartHour(9)
      setPreset('daily')
      setInterval(1)
      setIntervalUnit('day')
      setDaysOfWeek([])
      setMonthDayOfMonth(null)
      setMonthWeekdayRule(null)
      setMaxRuns(null)
      setEndMode('none')
      setEndDate('')
      setTimezone(getUserTimezone())
      setBookmark(false)
      setEmailNotification(false)
      setIsEditingName(true)
      setIsEditingDescription(false)
    } catch (error) {
      console.error('Failed to create scheduled task:', error)
      toast.error('Failed to schedule task. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="font-space-grotesk flex max-h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-3xl bg-white shadow-2xl">
        <div className="flex-shrink-0 px-8 pt-6">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="mb-3 flex items-center gap-3">
                {isEditingName ? (
                  <input
                    ref={nameInputRef}
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    onBlur={() => {
                      if (name.trim()) {
                        setIsEditingName(false)
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault()
                        if (name.trim()) {
                          setIsEditingName(false)
                        }
                      } else if (e.key === 'Escape') {
                        setName('')
                        setIsEditingName(false)
                      }
                    }}
                    onClick={(e) => e.stopPropagation()}
                    placeholder={`Scheduled Task`}
                    required
                    className={cn(
                      'min-w-0 flex-1 rounded border border-gray-300 bg-white px-2 py-1 text-2xl font-semibold text-gray-900 focus:border-gray-500 focus:outline-none'
                    )}
                  />
                ) : (
                  <div className="group flex min-w-0 flex-1 items-center gap-2">
                    <h2
                      className="line-clamp-1 min-w-0 flex-1 cursor-pointer text-2xl font-semibold text-gray-900"
                      onClick={() => setIsEditingName(true)}
                    >
                      {name || 'Scheduled Task'}
                    </h2>
                    <motion.button
                      onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        setIsEditingName(true)
                      }}
                      className="flex-shrink-0 rounded p-1 text-gray-400 opacity-0 transition-opacity hover:bg-gray-100 hover:text-gray-600 group-hover:opacity-100"
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.95 }}
                      aria-label="Edit name"
                    >
                      <Pencil className="h-4 w-4" />
                    </motion.button>
                  </div>
                )}
              </div>

              {/* Tool Calls Chips - Only show if there are tool calls */}
              {resolvedToolCalls.length > 0 && (
                <div className="mt-3 flex items-center gap-2">
                  <span className="flex-shrink-0 text-xs text-gray-600">Tools:</span>
                  <div className="flex min-w-0 flex-1 gap-2 overflow-x-auto overflow-y-visible pb-1">
                    {resolvedToolCalls.map((toolCall, index) => (
                      <ToolChip
                        key={index}
                        toolCall={toolCall}
                        isOpen={openToolIndex === index}
                        onToggle={() => setOpenToolIndex(openToolIndex === index ? null : index)}
                      />
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-2">
                {isEditingDescription ? (
                  <textarea
                    ref={descriptionTextareaRef}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    onBlur={() => setIsEditingDescription(false)}
                    onKeyDown={(e) => {
                      if (e.key === 'Escape') {
                        setDescription('')
                        setIsEditingDescription(false)
                      }
                    }}
                    onClick={(e) => e.stopPropagation()}
                    placeholder="Description (optional)"
                    rows={2}
                    className={cn(
                      'w-full rounded border border-gray-300 bg-white px-2 py-1 text-sm text-gray-900 focus:border-gray-500 focus:outline-none'
                    )}
                  />
                ) : (
                  <div className="group flex min-w-0 items-start gap-2">
                    {description ? (
                      <p className="min-w-0 flex-1 text-sm text-gray-600">{description}</p>
                    ) : (
                      <p className="min-w-0 flex-1 text-sm italic text-gray-400">No description</p>
                    )}
                    <motion.button
                      onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        setIsEditingDescription(true)
                      }}
                      className="mt-0.5 flex-shrink-0 rounded p-1 text-gray-400 opacity-0 transition-opacity hover:bg-gray-100 hover:text-gray-600 group-hover:opacity-100"
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.95 }}
                      aria-label="Edit description"
                    >
                      <Pencil className="h-3 w-3" />
                    </motion.button>
                  </div>
                )}
              </div>
            </div>
            <button
              onClick={onClose}
              className="ml-4 rounded-full p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
              type="button"
            >
              <X size={24} />
            </button>
          </div>
        </div>

        <form
          onSubmit={handleSubmit}
          className="flex min-h-0 flex-1 flex-col overflow-y-auto px-8 py-6"
        >
          <div className="mb-4 flex items-center gap-2">
            <Calendar size={16} className="text-gray-600" />
            <label className="text-sm font-medium text-gray-700">When should it run?</label>
          </div>
          <ScheduleEditor
            startDate={startDate}
            startHour={startHour}
            timezone={timezone}
            preset={preset}
            interval={interval}
            intervalUnit={intervalUnit}
            daysOfWeek={daysOfWeek}
            monthDayOfMonth={monthDayOfMonth}
            monthWeekdayRule={monthWeekdayRule}
            maxRuns={maxRuns}
            endMode={endMode}
            endDate={endDate}
            onStartDateChange={setStartDate}
            onStartHourChange={setStartHour}
            onTimezoneChange={setTimezone}
            onPresetChange={setPreset}
            onIntervalChange={setInterval}
            onIntervalUnitChange={setIntervalUnit}
            onDaysOfWeekChange={setDaysOfWeek}
            onMonthDayOfMonthChange={setMonthDayOfMonth}
            onMonthWeekdayRuleChange={setMonthWeekdayRule}
            onMaxRunsChange={setMaxRuns}
            onEndModeChange={setEndMode}
            onEndDateChange={setEndDate}
          />

          {/* Options */}
          <div className="mt-6 flex items-center gap-6">
            <button
              type="button"
              onClick={() => setBookmark((v) => !v)}
              className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"
            >
              <Star
                size={16}
                className={bookmark ? 'fill-yellow-400 text-yellow-400' : 'text-gray-400'}
              />
              Favourite
            </button>
            <button
              type="button"
              onClick={() => setEmailNotification((v) => !v)}
              className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"
            >
              <Bell
                size={16}
                className={emailNotification ? 'fill-blue-500 text-blue-500' : 'text-gray-400'}
              />
              Email on completion
            </button>
          </div>

          {/* Footer */}
          <div className="mt-6 flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="rounded-2xl border border-gray-200 bg-white px-6 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <div className="group relative">
              <button
                type="submit"
                disabled={isSubmitting || !name.trim() || isEditingName}
                className="rounded-2xl bg-black px-6 py-2 text-sm font-medium text-white hover:bg-gray-800 disabled:bg-gray-400"
                aria-disabled={isSubmitting || !name.trim() || isEditingName}
                aria-label={
                  isSubmitting || !name.trim() || isEditingName
                    ? 'Please enter a name'
                    : 'Schedule Task'
                }
              >
                {isSubmitting ? 'Scheduling...' : 'Schedule Task'}
              </button>
              {(isSubmitting || !name.trim() || isEditingName) && (
                <div className="pointer-events-none absolute left-1/2 top-[-36px] z-50 w-full -translate-x-1/2 rounded bg-black px-3 py-1 text-xs text-white opacity-0 transition-opacity group-hover:opacity-100">
                  Please enter a name
                </div>
              )}
            </div>
          </div>
        </form>
      </div>
    </Modal>
  )
}
